import {
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonModal,
    IonSpinner,
    IonToolbar,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import classNames from 'classnames';
import { useAtom } from 'jotai';
// @ts-ignore
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import React, { useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { accessTokenAtom, jobIdAtom } from '../../store/Atoms';
import { ROOT_URL } from '../../utils/constants';
import classes from './DocumentViewerModal.module.css';

export enum DocumentNames {
    QUOTE = 'quote_cadre_de_contribution',
    HONOUR_DECLARATION = 'attestation_sur_lhoneur',
    CEE_INVOICE = 'facture_cee',
}

interface DocumentViewerProps {
    documentName: string | number;
    onDocumentOpened?: (documentOpened: boolean) => void;
    onModalClosed: () => void;
    isOpen: boolean;
}

pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const panningSettings = {
    allowLeftClickPan: false,
    allowRightClickPan: false,
    velocityDisabled: true,
    disabled: true,
};

const wheelSettings = {
    smoothStep: 0.01,
    wheelDisabled: true,
};

const pinchSettings = {
    step: 20,
};

const DocumentViewerModal: React.FunctionComponent<DocumentViewerProps> = ({
    documentName,
    onDocumentOpened,
    isOpen,
    onModalClosed,
}) => {
    const [numPages, setNumPages] = useState<undefined | number>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [jobId] = useAtom(jobIdAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const documentURL = `${ROOT_URL}/media/interventions/${jobId}/documents/${documentName}?jwt=${accessToken}`;
    const [pdfURL, setPdfURL] = useState<null | string>(documentURL);
    const modal = useRef<HTMLIonModalElement>(null);
    const scale = (window.innerWidth * 1.55) / 1000;

    const PanningInteractionHandler = (instance: any, state: any) => {
        if (
            state?.positionX >= 0 &&
            state?.positionY >= 0 &&
            state?.scale <= 1
        ) {
            instance.setup.panning.disabled = true;
        } else {
            instance.setup.panning.disabled = false;
        }
    };

    const onDocumentLoadSuccess = ({
        numPages,
    }: {
        numPages: number;
    }): void => {
        setNumPages(numPages);
        setIsLoading(false);
        onDocumentOpened && onDocumentOpened(true);
    };

    const onDocumentLoadError = () => {
        setPdfURL(null);
        setIsLoading(false);
    };

    const retryHandler = async () => {
        setIsLoading(true);
        setPdfURL(documentURL);
    };

    return (
        <IonModal
            ref={modal}
            className={classes.docSignatureModal}
            isOpen={isOpen}
            backdropDismiss={true}
            onWillPresent={() => {
                setIsLoading(true);
            }}
            onDidDismiss={() => {
                onModalClosed && onModalClosed();
                setPdfURL(documentURL);
            }}
        >
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="end">
                        <IonButton onClick={() => modal.current?.dismiss()}>
                            Close
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <div className={classes.docSignatureModalContainer}>
                    {!pdfURL && (
                        <>
                            <IonButton
                                className="ion-margin-top ion-padding-end ion-padding-start ion-margin-bottom"
                                onClick={retryHandler}
                            >
                                <Trans>Retry</Trans>
                            </IonButton>
                            <div
                                style={{
                                    textAlign: 'center',
                                    padding: '0 20px',
                                }}
                            >
                                <p>{t`An error occurred while retrieving the quote, please retry.`}</p>
                                <p>{t`If the error persists, contact your supervisor.`}</p>
                            </div>
                        </>
                    )}
                    {isLoading && (
                        <IonSpinner
                            className={classNames({
                                'ion-margin-top': true,
                                'justify-content-center': true,
                            })}
                            name="circular"
                        ></IonSpinner>
                    )}
                    {!!pdfURL && (
                        <div className="inner-scroll scroll-y overscroll">
                            <TransformWrapper
                                onZoomStop={(ref) => {
                                    PanningInteractionHandler(
                                        ref.instance,
                                        ref.state
                                    );
                                }}
                                onPanning={(ref) => {
                                    PanningInteractionHandler(
                                        ref.instance,
                                        ref.state
                                    );
                                }}
                                onInit={(ref) => {
                                    ref.instance.setup.panning.disabled = true;
                                }}
                                pinch={pinchSettings}
                                wheel={wheelSettings}
                                panning={panningSettings}
                            >
                                <TransformComponent>
                                    <Document
                                        file={pdfURL}
                                        loading={''}
                                        onLoadSuccess={onDocumentLoadSuccess}
                                        onLoadError={onDocumentLoadError}
                                        onError={onDocumentLoadError}
                                        noData={''}
                                    >
                                        <div className={classes.docLine}>
                                            {Array.from(
                                                Array(numPages),
                                                (_, i) => i + 1
                                            ).map((page) => {
                                                return (
                                                    <Page
                                                        key={page}
                                                        pageNumber={page}
                                                        scale={scale}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </Document>
                                </TransformComponent>
                            </TransformWrapper>
                        </div>
                    )}
                </div>
            </IonContent>
        </IonModal>
    );
};

export default DocumentViewerModal;
